import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  getCommodityDutyRates,
  getCommodityTree,
  getOrderForms,
  getQaData,
  getQaPreferences,
  postCheckPreferentialValidation,
  postValidateRex,
} from '../../../api/v2/load';
import { putNeeNor } from '../../../api/v2/neenor';

const qaItemsQueryKeys = {
  orderForms: (loadId) => ['v2', 'getOrderForms', String(loadId)],
  qaData: (loadId) => ['v2', 'qadata', String(loadId)],
  qaPreferences: (loadId) => ['v2', 'qaPreferences', String(loadId)],
  commodityTree: (commodityCode) => ['v2', 'commodityTree', String(commodityCode)],
  commodityDutyRates: (items, vatAdj, roiLoad) => ['v2', 'commodityDutyRates', JSON.stringify(items), vatAdj, roiLoad],
  qaPrefValidation: (items) => ['v2', 'qaPrefValidation', JSON.stringify(items)],
};

export const useOrderForms = (loadId) =>
  useQuery({
    queryKey: qaItemsQueryKeys.orderForms(loadId),
    queryFn: () => getOrderForms(loadId),
    enabled: !!loadId,
  });

export const useCommodityTree = (commodityCode) =>
  useQuery({
    queryKey: qaItemsQueryKeys.commodityTree(commodityCode),
    queryFn: () => getCommodityTree(commodityCode),
    enabled: !!commodityCode,
  });

export const useQaItemsActions = () => {
  const queryClient = useQueryClient();

  const fetchOrderForms = (loadId) => {
    if (!loadId) return Promise.resolve(null);
    return queryClient.fetchQuery({
      queryKey: qaItemsQueryKeys.orderForms(loadId),
      queryFn: () => getOrderForms(loadId),
    });
  };

  const fetchQaData = (loadId) => {
    if (!loadId) return Promise.resolve(null);
    return queryClient.fetchQuery({
      queryKey: qaItemsQueryKeys.qaData(loadId),
      queryFn: () => getQaData(loadId),
    });
  };

  const fetchQaPreferences = (loadId) => {
    if (!loadId) return Promise.resolve(null);
    return queryClient.fetchQuery({
      queryKey: qaItemsQueryKeys.qaPreferences(loadId),
      queryFn: () => getQaPreferences(loadId),
    });
  };

  const fetchCommodityTree = (commodityCode) => {
    if (!commodityCode) return Promise.resolve(null);
    return queryClient.fetchQuery({
      queryKey: qaItemsQueryKeys.commodityTree(commodityCode),
      queryFn: () => getCommodityTree(commodityCode),
    });
  };

  const fetchCommodityDutyRates = (items, vatAdj = null, roiLoad = null) =>
    queryClient.fetchQuery({
      queryKey: qaItemsQueryKeys.commodityDutyRates(items, vatAdj, roiLoad),
      queryFn: () => getCommodityDutyRates(items, vatAdj, roiLoad),
    });

  const fetchPreferentialValidation = (items) =>
    queryClient.fetchQuery({
      queryKey: qaItemsQueryKeys.qaPrefValidation(items),
      queryFn: () => postCheckPreferentialValidation(items),
    });

  const validateRex = useMutation({
    mutationFn: (items) => postValidateRex(items),
  });

  const updateNeeNor = useMutation({
    mutationFn: (neenor) => putNeeNor(neenor),
  });

  return {
    fetchOrderForms,
    fetchQaData,
    fetchQaPreferences,
    fetchCommodityTree,
    fetchCommodityDutyRates,
    fetchPreferentialValidation,
    validateRex,
    updateNeeNor,
  };
};
